/**
 * TypeScript interfaces for the Action Detail Modal feature.
 * Defines data structures for:
 * - API request/response shapes
 * - Action data structures
 * - Component props
 * - Filter state management
 *
 * Parallels ShotDetailModal/types.ts but for action-level data.
 */

import {
  ShotQueryContext,
  Shot,
  normalizeShot,
  ShotFilters,
  DEFAULT_FILTERS as DEFAULT_SHOT_FILTERS,
} from "../ShotDetailModal/types";
import type { QueryResolution } from "../../models/Explorer";

// ============================================================================
// Action Type Enums
// ============================================================================

/**
 * Action types as returned by the API (trailing 's' from database).
 * Request uses singular ("pick"), response uses plural ("picks").
 */
export type ActionType =
  | "picks"
  | "handoffs"
  | "isolations"
  | "posts"
  | "drives"
  | "off_ball_screens"
  | "fake_handoffs"
  | "no_action";

export const ALL_ACTION_TYPES: ActionType[] = [
  "picks",
  "handoffs",
  "isolations",
  "posts",
  "drives",
  "off_ball_screens",
  "fake_handoffs",
];

/**
 * Size matchup classification.
 */
export type ActionSize =
  | "big"
  | "small"
  | "small_small"
  | "small_big"
  | "big_big"
  | "big_small"
  | "none";

export const ALL_ACTION_SIZES: ActionSize[] = [
  "big",
  "small",
  "small_small",
  "small_big",
  "big_big",
  "big_small",
  "none",
];

/**
 * Primary outcome of an action.
 */
export type PrimaryOutcome = "finish" | "pass";

// ============================================================================
// Type-Specific Field Configuration
// ============================================================================

/**
 * Configuration for a type-specific field that can be used as a filter.
 */
export interface TypeSpecificFieldConfig {
  /** Field name on the Action interface */
  field: string;
  /** Display label for the filter group */
  label: string;
  /** Whether the field is a string (chip group) or boolean (toggle chips) */
  type: "string" | "boolean";
}

/**
 * Type-specific filter fields for each action type.
 * These filters only appear when all visible actions share a single actionType.
 * Boolean fields are displayed as "Yes"/"No" chip toggles.
 */
export const TYPE_SPECIFIC_FIELDS: Record<
  ActionType,
  TypeSpecificFieldConfig[]
> = {
  // Groups whose key a source lacks (scrDefenseDetail / doubleScreenType on ss)
  // hide themselves: a group needs two or more values to show.
  picks: [
    { field: "pickType", label: "Pick Type", type: "string" },
    { field: "pickLocation", label: "Pick Location", type: "string" },
    { field: "pickScreenDirection", label: "Screen Direction", type: "string" },
    { field: "pickSpacing", label: "Spacing", type: "string" },
    { field: "doubleScreenType", label: "Double Screen", type: "string" },
    { field: "pickSide", label: "Pick Side", type: "string" },
    { field: "direction", label: "Direction", type: "string" },
    { field: "pickDirectionMiddleSide", label: "Direction Middle/Side", type: "string" },
    { field: "emptyCorner", label: "Empty Corner", type: "boolean" },
    { field: "bhrDefense", label: "BH Defense", type: "string" },
    { field: "scrDefense", label: "Screener Defense", type: "string" },
    { field: "coverageReason", label: "Coverage (reason)", type: "string" },
    { field: "scrDefenseDetail", label: "Screener Defense (detail)", type: "string" },
    { field: "rollType", label: "Roll Type", type: "string" },
    { field: "pickOutcome", label: "Pick Outcome", type: "string" },
  ],
  handoffs: [
    { field: "handoffSide", label: "Side", type: "string" },
    { field: "handoffDirection", label: "Direction", type: "string" },
    { field: "handoffDirectionMiddleSide", label: "Direction Middle/Side", type: "string" },
    { field: "handoffEmptyCorner", label: "Empty Corner", type: "boolean" },
    { field: "receiverDefType", label: "Receiver Defense", type: "string" },
    { field: "setterDefType", label: "Setter Defense", type: "string" },
    { field: "handoffType", label: "Type", type: "string" },
  ],
  isolations: [
    { field: "isoSide", label: "Iso Side", type: "string" },
    { field: "isoDriveDirection", label: "Drive Direction", type: "string" },
    { field: "doubled", label: "Doubled", type: "boolean" },
  ],
  posts: [
    { field: "postSection", label: "Post Section", type: "string" },
    { field: "postOutcome", label: "Post Outcome", type: "string" },
    { field: "doubleTeam", label: "Double Team", type: "boolean" },
    { field: "dribbledInto", label: "Dribbled Into", type: "boolean" },
  ],
  drives: [
    // driveFamily is always "misc" in this chart (decision S), so no group for it.
    { field: "driveContext", label: "Drive Context", type: "string" },
    { field: "driveSide", label: "Side", type: "string" },
    { field: "driveDirection", label: "Drive Direction", type: "string" },
    { field: "blowby", label: "Blowby", type: "boolean" },
    { field: "endType", label: "End Type", type: "string" },
  ],
  off_ball_screens: [
    { field: "screenType", label: "Screen Type", type: "string" },
    { field: "cutterDefType", label: "Cutter Defense", type: "string" },
    { field: "obsSide", label: "Side", type: "string" },
    { field: "ledToTouch", label: "Led to Touch", type: "boolean" },
    { field: "ledToShot", label: "Led to Shot", type: "boolean" },
  ],
  fake_handoffs: [],
  no_action: [],
};

// ============================================================================
// Action Interface
// ============================================================================

/**
 * Individual action data from the API response.
 * Common fields are always present; type-specific fields are only present
 * for the relevant action type (omitted, not null, due to Snowflake OBJECT_CONSTRUCT).
 */
export interface Action {
  /** Court x-coordinate (0-50, where 25 is center court) */
  x: number;
  /** Court y-coordinate (0-47, where 0 is baseline) */
  y: number;
  /** Raw action type (plural form from database) */
  actionType: ActionType;
  /** Simplified action classification: "PR", "DHO", "Iso", etc. (+ variants) */
  actionSimple: string;
  /** Size matchup classification */
  actionSize: ActionSize;
  /** Whether this was the last action before shot/turnover. Can be false or null. */
  direct: boolean | null;
  /** Primary player index into ActionDetailResponse.players (role varies by type — BH for picks, receiver for handoffs, etc.) */
  primaryPlayer: number | null;
  /** Secondary player index (null for iso/post/drive) */
  secondaryPlayer: number | null;
  /** Defender on primary player (index into players table) */
  primaryDefender: number | null;
  /** Defender on secondary player (index into players table) */
  secondaryDefender: number | null;
  /** What the primary player did: "finish" or "pass" */
  primaryOutcome: PrimaryOutcome | null;
  /** Outcome for secondary player */
  secondaryOutcome: string | null;
  /** Actual points for the possession. CHANCE-LEVEL — deduplicate by chanceId. */
  aPts: number | null;
  /** Expected points for the possession. CHANCE-LEVEL — deduplicate by chanceId. */
  ePts: number | null;
  /** How the possession ended: "FG2", "FG3", "FGX", "TO", etc. CHANCE-LEVEL. */
  chanceOutcome: string | null;
  /** Whether the chance counts toward PPP / xPPP (the report's valid chances: not a timeout, stoppage, non-shooting
   * foul, ...). CHANCE-LEVEL. null on a response from before the flag existed: counted, as before. */
  valid: boolean | null;
  /** Position of this action within the possession sequence (1-based) */
  orderInChance: number;
  /** Unique possession identifier. Used for aPPP deduplication and action grouping. */
  chanceId: string | null;
  /** Possession ID — groups chances within the same possession (e.g., ORB creates new chance, same possession) */
  possessionId: string | null;

  // Chance-level classification fields
  /** Whether this chance is transition or halfcourt */
  transition: string | null;
  /** Chance type classification (dynamic values from backend) */
  chanceType: string | null;
  /** Defensive coverage: zone or man */
  zone: string | null;
  /** Rebound outcome for this chance: "offensive", "defensive", or null */
  rebounded: string | null;

  // Type-specific fields accessed via optional chaining
  // Pick fields
  pickType?: string;
  bhrDefense?: string;
  scrDefense?: string;
  pickOutcome?: string;
  direction?: string;
  rollType?: string;
  pickSide?: string;
  emptyCorner?: boolean;
  pickDirectionMiddleSide?: string;
  /** scrDefense with `none` split into rejected / slipped / taken (R3) */
  coverageReason?: string;
  /** CTG's six screener-coverage buckets — ctg only */
  scrDefenseDetail?: string;
  /** PickType v2 components (R3) */
  pickLocation?: string;
  pickScreenDirection?: string;
  pickSpacing?: string;
  /** stagger / horns for picks in a double screen — ctg only */
  doubleScreenType?: string;
  // Handoff fields
  handoffType?: string;
  receiverDefType?: string;
  setterDefType?: string;
  handoffSide?: string;
  handoffDirection?: string;
  handoffDirectionMiddleSide?: string;
  handoffEmptyCorner?: boolean;
  // Isolation fields
  isoSide?: string;
  isoDriveDirection?: string;
  doubled?: boolean;
  // Shared fields (used by multiple action types)
  doubleTeam?: boolean;
  // Post fields
  postSection?: string;
  postOutcome?: string;
  dribbledInto?: boolean;
  // Off-ball screen fields
  screenType?: string;
  cutterDefType?: string;
  obsSide?: string;
  ledToTouch?: boolean;
  ledToShot?: boolean;
  // Drive fields
  driveDirection?: string;
  blowby?: boolean;
  endType?: string;
  driveSide?: string;
  driveMiddle?: boolean;
  /** misc / pick / iso / post; always "misc" in the action chart */
  driveFamily?: string;
  /** misc / closeout / off_ball_screen / fake_handoff (R3) */
  driveContext?: string;
  /** Seconds remaining in the period at the action (R3) */
  gameClock?: number;
}

/**
 * Shot data linked to actions via chanceId.
 * Included in the action detail response for future toggle/blended views.
 */
export interface ActionShot {
  x: number;
  y: number;
  made: boolean;
  assisted: boolean;
  fouled: boolean;
  three: boolean;
  ptsScored: number;
  shotType: string | null;
  complexShotType: string | null;
  contestLevel: string | null;
  catchAndShoot: boolean | null;
  region: string | null;
  /** Shooter index into ActionDetailResponse.players (resolved to name when converted to Shot) */
  shooter: number | null;
  /** Passer index into ActionDetailResponse.players */
  passer: number | null;
  /** Closest contester index into ActionDetailResponse.players (absent when uncontested) */
  contester?: number | null;
  chanceId: string | null;
}

/**
 * Chance-keyed lineup map: maps chance id → { off, def } where each side is a
 * list of player indices into ActionDetailResponse.players.
 */
export interface ChanceLineup {
  off: number[];
  def: number[];
}

// ============================================================================
// API Types
// ============================================================================

/**
 * Full API response from POST /api/team_report/action_detail
 */
export interface ActionDetailResponse {
  actions: Action[];
  shots: ActionShot[];
  /** Interned player names; Action and ActionShot reference these by index. */
  players: string[];
  /** Per-chance lineups keyed by chanceId. Indices point into `players`. */
  lineups: Record<string, ChanceLineup>;
  total: number;
  totalShots: number;
  queryTimeSeconds: number;
  /** The sources the chart read (R3); dev-only label, never product UI. */
  resolution?: QueryResolution;
}

// ============================================================================
// Normalization
// ============================================================================

const VALID_ACTION_TYPES = new Set<string>([...ALL_ACTION_TYPES, "no_action"]);
const VALID_ACTION_SIZES = new Set<string>(ALL_ACTION_SIZES);
const VALID_PRIMARY_OUTCOMES = new Set<string>(["finish", "pass"]);

/**
 * Normalize an action from the API response.
 * The API omits null fields (Snowflake OBJECT_CONSTRUCT), so we use ?? null
 * to ensure all common fields have explicit null rather than undefined.
 */
export function normalizeAction(raw: any): Action {
  return {
    x: raw.x ?? 0,
    y: raw.y ?? 0,
    actionType: VALID_ACTION_TYPES.has(raw.actionType)
      ? raw.actionType
      : "picks", // fallback
    actionSimple: raw.actionSimple ?? "",
    actionSize: VALID_ACTION_SIZES.has(raw.actionSize)
      ? raw.actionSize
      : "none",
    direct: typeof raw.direct === "boolean" ? raw.direct : null,
    primaryPlayer: typeof raw.primaryPlayer === "number" ? raw.primaryPlayer : null,
    secondaryPlayer: typeof raw.secondaryPlayer === "number" ? raw.secondaryPlayer : null,
    primaryDefender: typeof raw.primaryDefender === "number" ? raw.primaryDefender : null,
    secondaryDefender: typeof raw.secondaryDefender === "number" ? raw.secondaryDefender : null,
    primaryOutcome: VALID_PRIMARY_OUTCOMES.has(raw.primaryOutcome)
      ? raw.primaryOutcome
      : null,
    secondaryOutcome: raw.secondaryOutcome ?? null,
    aPts: typeof raw.aPts === "number" ? raw.aPts : null,
    ePts: typeof raw.ePts === "number" ? raw.ePts : null,
    chanceOutcome: raw.chanceOutcome ?? null,
    valid: typeof raw.valid === "boolean" ? raw.valid : null,
    orderInChance: raw.orderInChance ?? 1,
    chanceId: raw.chanceId ?? null,
    possessionId: raw.possessionId ?? null,
    transition: raw.transition ?? null,
    chanceType: raw.chanceType ?? null,
    zone: raw.zone ?? null,
    rebounded: raw.rebounded ?? null,
    // Pass through type-specific fields as-is (accessed via optional chaining)
    ...(raw.pickType !== undefined && { pickType: raw.pickType }),
    ...(raw.bhrDefense !== undefined && { bhrDefense: raw.bhrDefense }),
    ...(raw.scrDefense !== undefined && { scrDefense: raw.scrDefense }),
    ...(raw.pickOutcome !== undefined && { pickOutcome: raw.pickOutcome }),
    ...(raw.direction !== undefined && { direction: raw.direction }),
    ...(raw.rollType !== undefined && { rollType: raw.rollType }),
    ...(raw.pickSide !== undefined && { pickSide: raw.pickSide }),
    ...(raw.emptyCorner !== undefined && { emptyCorner: raw.emptyCorner }),
    ...(raw.pickDirectionMiddleSide !== undefined && {
      pickDirectionMiddleSide: raw.pickDirectionMiddleSide,
    }),
    ...(raw.coverageReason !== undefined && {
      coverageReason: raw.coverageReason,
    }),
    ...(raw.scrDefenseDetail !== undefined && {
      scrDefenseDetail: raw.scrDefenseDetail,
    }),
    ...(raw.pickLocation !== undefined && { pickLocation: raw.pickLocation }),
    ...(raw.pickScreenDirection !== undefined && {
      pickScreenDirection: raw.pickScreenDirection,
    }),
    ...(raw.pickSpacing !== undefined && { pickSpacing: raw.pickSpacing }),
    ...(raw.doubleScreenType !== undefined && {
      doubleScreenType: raw.doubleScreenType,
    }),
    ...(raw.handoffType !== undefined && { handoffType: raw.handoffType }),
    ...(raw.receiverDefType !== undefined && {
      receiverDefType: raw.receiverDefType,
    }),
    ...(raw.setterDefType !== undefined && {
      setterDefType: raw.setterDefType,
    }),
    ...(raw.handoffSide !== undefined && { handoffSide: raw.handoffSide }),
    ...(raw.handoffDirection !== undefined && {
      handoffDirection: raw.handoffDirection,
    }),
    ...(raw.handoffDirectionMiddleSide !== undefined && {
      handoffDirectionMiddleSide: raw.handoffDirectionMiddleSide,
    }),
    ...(raw.handoffEmptyCorner !== undefined && {
      handoffEmptyCorner: raw.handoffEmptyCorner,
    }),
    ...(raw.isoSide !== undefined && { isoSide: raw.isoSide }),
    ...(raw.isoDriveDirection !== undefined && {
      isoDriveDirection: raw.isoDriveDirection,
    }),
    ...(raw.doubled !== undefined && { doubled: raw.doubled }),
    ...(raw.doubleTeam !== undefined && { doubleTeam: raw.doubleTeam }),
    ...(raw.postSection !== undefined && { postSection: raw.postSection }),
    ...(raw.postOutcome !== undefined && { postOutcome: raw.postOutcome }),
    ...(raw.dribbledInto !== undefined && { dribbledInto: raw.dribbledInto }),
    ...(raw.screenType !== undefined && { screenType: raw.screenType }),
    ...(raw.cutterDefType !== undefined && {
      cutterDefType: raw.cutterDefType,
    }),
    ...(raw.obsSide !== undefined && { obsSide: raw.obsSide }),
    ...(raw.ledToTouch !== undefined && { ledToTouch: raw.ledToTouch }),
    ...(raw.ledToShot !== undefined && { ledToShot: raw.ledToShot }),
    ...(raw.driveDirection !== undefined && {
      driveDirection: raw.driveDirection,
    }),
    ...(raw.blowby !== undefined && { blowby: raw.blowby }),
    ...(raw.endType !== undefined && { endType: raw.endType }),
    ...(raw.driveSide !== undefined && { driveSide: raw.driveSide }),
    ...(raw.driveMiddle !== undefined && { driveMiddle: raw.driveMiddle }),
    ...(raw.driveFamily !== undefined && { driveFamily: raw.driveFamily }),
    ...(raw.driveContext !== undefined && { driveContext: raw.driveContext }),
    ...(typeof raw.gameClock === "number" && { gameClock: raw.gameClock }),
  };
}

// ============================================================================
// Filter Types
// ============================================================================

/**
 * Filter state for the action detail modal.
 * Each category uses a Set to allow multiple selections (OR logic within category).
 * Selections across categories use AND logic.
 *
 * Phase 1: Common filters only. Type-specific filters deferred to Phase 2.
 */
export type LineupMode = "any" | "all";

export type LineupKey =
  | "offenseOnCourt"
  | "offenseOffCourt"
  | "defenseOnCourt"
  | "defenseOffCourt";

export interface ActionFilters {
  /** Chance-level filter: include real actions ("action") and/or non-action chances ("no_action") */
  actionPresence: Set<"action" | "no_action">;
  /** Filter by transition vs halfcourt (empty = all) */
  transition: Set<string>;
  /** Filter by chance type classification (empty = all) */
  chanceType: Set<string>;
  /** Filter by defensive coverage: zone vs man (empty = all) */
  zone: Set<string>;
  /** Filter by action type */
  actionType: Set<ActionType>;
  /** Filter by simplified action classification (empty = all) */
  actionSimple: Set<string>;
  /** Filter by size matchup */
  actionSize: Set<ActionSize>;
  /** Filter by direct/indirect */
  direct: Set<"direct" | "indirect">;
  /** Filter by primary outcome */
  primaryOutcome: Set<PrimaryOutcome>;
  /** Filter by primary player index (empty = all) */
  primaryPlayer: Set<number>;
  /** Filter by secondary player index (empty = all) */
  secondaryPlayer: Set<number>;
  /** Filter by primary defender index (empty = all) */
  primaryDefender: Set<number>;
  /** Filter by secondary defender index (empty = all) */
  secondaryDefender: Set<number>;
  /** Lineup filter: selected offensive players required on court (empty = all) */
  offenseOnCourt: Set<number>;
  /** Lineup filter: selected offensive players required off court (empty = all) */
  offenseOffCourt: Set<number>;
  /** Lineup filter: selected defensive players required on court (empty = all) */
  defenseOnCourt: Set<number>;
  /** Lineup filter: selected defensive players required off court (empty = all) */
  defenseOffCourt: Set<number>;
  /** Per-axis mode for lineup filters. Default "all" for all four axes. */
  lineupMode: Record<LineupKey, LineupMode>;
  /**
   * Dynamic type-specific filters. Key is field name, value is selected string values.
   * Boolean fields use "true"/"false" as values. Empty set = all (no filtering).
   */
  typeSpecific: Record<string, Set<string>>;
}

export const LINEUP_KEYS: LineupKey[] = [
  "offenseOnCourt",
  "offenseOffCourt",
  "defenseOnCourt",
  "defenseOffCourt",
];

export function isLineupKey(key: string): key is LineupKey {
  return (
    key === "offenseOnCourt" ||
    key === "offenseOffCourt" ||
    key === "defenseOnCourt" ||
    key === "defenseOffCourt"
  );
}

/**
 * Default filter state with all options selected (show all actions).
 */
export const DEFAULT_ACTION_FILTERS: ActionFilters = {
  actionPresence: new Set<"action" | "no_action">(["action", "no_action"]),
  transition: new Set<string>(), // Empty = all
  chanceType: new Set<string>(), // Empty = all
  zone: new Set<string>(), // Empty = all
  actionType: new Set<ActionType>(ALL_ACTION_TYPES),
  actionSimple: new Set<string>(), // Empty = all
  actionSize: new Set<ActionSize>(ALL_ACTION_SIZES),
  direct: new Set<"direct" | "indirect">(["direct", "indirect"]),
  primaryOutcome: new Set<PrimaryOutcome>(["finish", "pass"]),
  primaryPlayer: new Set<number>(), // Empty = all
  secondaryPlayer: new Set<number>(), // Empty = all
  primaryDefender: new Set<number>(), // Empty = all
  secondaryDefender: new Set<number>(), // Empty = all
  offenseOnCourt: new Set<number>(),
  offenseOffCourt: new Set<number>(),
  defenseOnCourt: new Set<number>(),
  defenseOffCourt: new Set<number>(),
  lineupMode: {
    offenseOnCourt: "all",
    offenseOffCourt: "all",
    defenseOnCourt: "all",
    defenseOffCourt: "all",
  },
  typeSpecific: {}, // No type-specific filters active
};

// ============================================================================
// Component Props
// ============================================================================

/**
 * Props for the ActionDetailContent component (inner content, no modal shell).
 */
export interface ActionDetailContentProps {
  /** Query context for the API request (unused when directData is provided) */
  queryContext?: ShotQueryContext;
  /** Team NBA ID for the API request (unused when directData is provided) */
  teamId?: string;
  /** Team abbreviation (e.g. "POR") for explorer pin state */
  teamAbbrev?: string;
  /** Season year for the API request (unused when directData is provided) */
  season?: string;
  /** Season type for the API request (e.g., "regular season", "playoff") */
  seasonType?: string;
  /** Whether to show the college/G-League three-point line */
  showCollegeLine: boolean;
  /** Whether to show the xPPP column (false for SkillCorner/DRAFT environment) */
  showEPts?: boolean;
  /** Pre-parsed data — when provided, skips API fetch */
  directData?: ActionDetailResponse;
  /** Parent modal title for building video dialog titles */
  modalTitle?: string;
}

/**
 * Props for the ActionDetailChart component.
 */
export interface ActionDetailChartProps {
  /** Array of all actions (unfiltered) */
  actions: Action[];
  /** Per-chance lineup map keyed by chanceId (indices into players table) */
  lineups: Record<string, ChanceLineup>;
  /** Current filter state */
  filters: ActionFilters;
  /** Chart width in pixels */
  width?: number;
  /** Chart height in pixels */
  height?: number;
  /** Whether to show the college/G-League three-point line */
  showCollegeLine?: boolean;
  /** Actions to highlight on the chart (from summary row hover/selection). Null = no highlight. */
  highlightedActions?: Set<Action> | null;
}

/**
 * Props for the ActionDetailFilters component.
 */
export interface ActionDetailFiltersProps {
  /** All actions (unfiltered) for computing dynamic counts */
  actions: Action[];
  /** Per-chance lineup map keyed by chanceId (indices into players table) */
  lineups: Record<string, ChanceLineup>;
  /** Current filter state */
  filters: ActionFilters;
  /** Callback when filters change */
  onChange: (filters: ActionFilters) => void;
  /** Whether filters are disabled (during loading) */
  disabled?: boolean;
  /** Ordered array of group keys for hierarchical breakdown (empty = none) */
  expandedGroups: string[];
  /** Callback when a filter header is clicked. Receives group key + whether Cmd/Ctrl was held. */
  onExpandGroup: (groupKey: string, isModifierClick: boolean) => void;
  /** Callback to clear all expanded groups (used by reset button) */
  onResetGroups?: () => void;
  /** Callback when explorer icon is clicked on a filter group header. Receives the explorer dimension name. */
  onExplorerDimensionClick?: (dimension: string) => void;
  /** Key currently loading an explorer query (null = none) */
  explorerLoadingKey?: string | null;
}

/**
 * Props for the ActionDetailSummary component.
 */
export interface ActionDetailSummaryProps {
  /** Array of all actions (unfiltered) — will be filtered internally */
  actions: Action[];
  /** Shots linked via chanceId from filtered actions */
  linkedShots: Shot[];
  /**
   * Shots linked via chanceId from the pre-lineup-filter action set. Used for
   * lineup-key breakdowns where the "off" partition needs shots from chances
   * the active lineup filter would normally exclude.
   */
  rootLinkedShots: Shot[];
  /** Per-chance lineup map keyed by chanceId (indices into players table) */
  lineups: Record<string, ChanceLineup>;
  /** Current action filter state */
  filters: ActionFilters;
  /** Current shot filter state — shooting metrics reflect these filters */
  shotFilters: ShotFilters;
  /** Ordered array of group keys for hierarchical breakdown (empty = default single row) */
  expandedGroups: string[];
  /** Callback when a row is hovered (null = mouse left). Provides rowKey, action subset, and shot subset. */
  onRowHover?: (
    rowKey: string | null,
    actions: Action[] | null,
    shots?: Shot[],
  ) => void;
  /** Callback when a row is clicked. Provides rowKey, action subset, and shot subset for persistent selection. */
  onRowClick?: (rowKey: string, actions: Action[], shots?: Shot[]) => void;
  /** Currently selected row key (for visual indicator) */
  selectedRowKey?: string | null;
  /** Whether to show the xPPP column (default true) */
  showEPts?: boolean;
  /** Callback when video icon is clicked. Receives unique chance IDs and row label for title. */
  onVideoClick?: (
    chanceIds: string[],
    rowLabel: string,
    metaKey?: boolean,
  ) => void;
  /** Whether a video playlist is currently being generated */
  videoLoading?: boolean;
}

// ============================================================================
// Shot ↔ Action Bridging
// ============================================================================

/**
 * Convert an ActionShot (interned player indices from the action_detail API)
 * to a Shot (typed enums from ShotDetailModal, string player names). Resolves
 * the shooter/passer indices against the response's `players` table, then
 * delegates to normalizeShot() for enum validation.
 */
export function actionShotToShot(actionShot: ActionShot, players: string[]): Shot {
  const resolve = (idx: number | null): string | null =>
    idx === null || idx === undefined ? null : players[idx] ?? null;
  return normalizeShot({
    ...actionShot,
    shooter: resolve(actionShot.shooter),
    passer: resolve(actionShot.passer),
    contester: resolve(actionShot.contester ?? null),
  });
}

/**
 * Shot filter keys that can appear in the unified breakdown hierarchy.
 * Used to distinguish shot-level breakdowns from action-level breakdowns.
 */
export const SHOT_FILTER_KEYS = new Set([
  "assisted",
  "fouled",
  "shotType",
  "complexShotType",
  "contestLevel",
  "catchAndShoot",
  "regionCategory",
  "shooter",
  "passer",
  "contester",
]);

export function isShotFilterKey(key: string): boolean {
  return SHOT_FILTER_KEYS.has(key);
}

// Re-export shot filter types for convenience
export type { Shot, ShotFilters };
export { DEFAULT_SHOT_FILTERS };
